import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { CloseButton, ToastContent, ToastIcon, ToastItem, ToastList, ToastMessage, ToastTitle } from './Toast.styles';
import { addToast, getToasts, removeToast, subscribe, Toast } from './toastStore';

export interface ToastTypes {
  title?: string;
  message?: React.ReactNode;
  type?: 'info' | 'success' | 'warning' | 'error';
}

const AUTO_CLOSE_MS = 6000;
const CLOSE_ANIMATION_MS = 200;

const TYPE_ICON = {
  info: faInfoCircle,
  success: faCheckCircle,
  warning: faTriangleExclamation,
  error: faExclamationCircle,
};

export const openToast = ({ title, message, type }: ToastTypes) => {
  addToast({ title, message, type });
};

const ToastEntry: React.FC<{ toast: Toast }> = ({ toast }) => {
  const [closing, setClosing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const close = () => {
    setClosing(true);
    setTimeout(() => removeToast(toast.id), CLOSE_ANIMATION_MS);
  };

  const startTimer = () => {
    timeoutRef.current = setTimeout(close, AUTO_CLOSE_MS);
  };

  const stopTimer = () => clearTimeout(timeoutRef.current);

  useEffect(() => {
    startTimer();
    return stopTimer;
  }, []);

  const icon = toast.type && TYPE_ICON[toast.type];

  return (
    <ToastItem
      $closing={closing}
      onClick={close}
      onMouseEnter={stopTimer}
      onMouseLeave={startTimer}
      data-testid="toast"
    >
      {icon && (
        <ToastIcon>
          <FontAwesomeIcon icon={icon} color="#fff" size={18} />
        </ToastIcon>
      )}

      <ToastContent>
        {toast.title && <ToastTitle data-testid="toast-title">{toast.title}</ToastTitle>}
        {toast.message && <ToastMessage data-testid="toast-message">{toast.message}</ToastMessage>}
      </ToastContent>

      <CloseButton
        type="button"
        aria-label="Dismiss notification"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
      >
        <FontAwesomeIcon icon={faTimes} color="#fff" size={12} />
      </CloseButton>
    </ToastItem>
  );
};

const ToastProvider = () => {
  const toasts = useSyncExternalStore(subscribe, getToasts, getToasts);

  return (
    <ToastList>
      {toasts.map((toast) => (
        <ToastEntry key={toast.id} toast={toast} />
      ))}
    </ToastList>
  );
};

export default ToastProvider;
